import axios from "axios";
import LocalStorageService from "./storage";

const localStorageService = LocalStorageService();
const maintenancePagePath = "/maintenance";
const maintenanceRetryAfterStorageKey = "bcs_maintenance_retry_after";
const maintenancePreviousPathStorageKey = "bcs_maintenance_previous_path";

// Configuração global de timeout
axios.defaults.timeout = 30000;

function normalizeBaseUrl(url) {
  return (url || "").replace(/\/+$/, "");
}

function parseRetryAfter(retryAfter) {
  if (!retryAfter) {
    return null;
  }

  const retryAfterInSeconds = Number(retryAfter);

  if (Number.isFinite(retryAfterInSeconds)) {
    return Math.max(0, Math.ceil(retryAfterInSeconds));
  }

  const retryAfterDate = new Date(retryAfter).getTime();

  if (!Number.isNaN(retryAfterDate)) {
    return Math.max(0, Math.ceil((retryAfterDate - Date.now()) / 1000));
  }

  return null;
}

function getCurrentPath() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function redirectToMaintenancePage(retryAfterSeconds) {
  const currentPath = getCurrentPath();

  if (retryAfterSeconds !== null) {
    sessionStorage.setItem(maintenanceRetryAfterStorageKey, String(retryAfterSeconds));
  }

  if (window.location.pathname !== maintenancePagePath) {
    sessionStorage.setItem(maintenancePreviousPathStorageKey, currentPath);
    window.location.assign(maintenancePagePath);
  }
}

function createApiInstance(baseURL) {
  const instance = axios.create({
    baseURL
  });

  // Interceptador de requisições
  instance.interceptors.request.use(
    (config) => {
      const token = localStorageService.getIdToken();

      // Evita adicionar o token no header na rota de login
      if (!config.url?.includes("/login") && token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Interceptador de respostas
  instance.interceptors.response.use(
    (response) => response,
    async (err) => {
      const status = err?.response?.status;
      const errorMsg = err?.response?.data?.error;
      const message = err?.response?.data?.message;
      const retryAfter = err?.response?.headers?.["retry-after"] || err?.response?.headers?.get?.("retry-after");
      const retryAfterSeconds = parseRetryAfter(retryAfter);

      if (status === 503) {
        redirectToMaintenancePage(retryAfterSeconds);

        return Promise.resolve({
          success: false,
          data: [],
          message: "Sistema temporariamente em manutenção. Tente novamente em instantes.",
          status,
          isMaintenance: true,
          retryAfterSeconds
        });
      }

      const isTokenInvalid =
        (errorMsg === "Token Inválido" || errorMsg === "Token expirado" || message === "Unauthorized") &&
        status === 401;

      const isMissingToken = errorMsg === "Sem token" && status === 400;

      if (isTokenInvalid || isMissingToken) {
        localStorageService.logout();
        delete instance.defaults.headers.Authorization;
        window.location.href = "/";
      }

      if (err.response) {
        return Promise.resolve({
          success: false,
          data: [],
          message:
            err.response.data?.message ||
            err.response.data?.error ||
            `Erro ${status}: ${err.response.statusText}`,
          status
        });
      }

      if (err.request) {
        return Promise.resolve({
          success: false,
          data: [],
          message: "Servidor não respondeu. Verifique sua conexão.",
          status: 0
        });
      }

      // Erro inesperado
      return Promise.resolve({
        success: false,
        data: [],
        message: "Erro inesperado ao processar a requisição.",
        status: 0
      });
    }
  );

  return instance;
}

const legacyApiBaseUrl = normalizeBaseUrl(
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3000/api"
);

const backendV2Origin = normalizeBaseUrl(
  process.env.REACT_APP_BACKEND_V2_URL || "http://localhost:3000"
);

const api = createApiInstance(legacyApiBaseUrl);

const apiV2 = createApiInstance(`${backendV2Origin}/api/v2`);

function getApiClient(version = "v1") {
  return version === "v2" ? apiV2 : api;
}

export default api;
export { api, apiV2, getApiClient };
